import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Building2, Mail, Phone } from "lucide-react";
import { 
        getClientReferralList,
        doClientReferralUpdate,
        resetClientReferralFilters
 } from "../../../../redux/clientreferral/clientReferralState";
import { Card, Input, Pagination} from "antd";
import ButtonWithConfirm from "components/button/buttonwithconfirm";
import CustomTabs from "components/tabs/customTab";
import {
  CircleAlert,
  CircleCheck,
} from "lucide-react";

const ClientReferralList = () => {
  const dispatch = useDispatch();

  const recordList = useSelector((state) => state.clientReferral.clientReferralList);
  const filters = useSelector((state) => state.clientReferral.clientReferralList.filters);
  const contactStatuses = recordList.contactStatusOptions || [];
  const PIPELINE = contactStatuses.filter((status) => status.description === "pipeline");
  const CLOSED = contactStatuses.filter((status) => status.description === "closed");

  const [activeTab, setActiveTab] = useState(
    filters?.status_group || "all"
  );

  useEffect(() => {
    return () => {
      dispatch(resetClientReferralFilters());
    };
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getClientReferralList({
        page: 1,
        paginate: 5,
        status_group: activeTab,
      })
    );
  }, [dispatch, activeTab]);

  const handlePageChange = (page, pageSize) => {
    dispatch(
      getClientReferralList({
        ...filters,
        page,
        paginate: pageSize,
      })
    );
  };

  const changeStatus = (record, status) => {
    dispatch(
      doClientReferralUpdate({
        crf_id: record.crf_id,
        action: "update_status",
        crf_status_id: status.value,
      })
    );
  };

  const updateRemarks = (record, remarks) => {
    const trimmedRemarks = remarks.trim();

    if (trimmedRemarks === (record.crf_remarks || "")) {
      return;
    }

    dispatch(
      doClientReferralUpdate({
        crf_id: record.crf_id,
        crf_remarks: trimmedRemarks,
      })
    );
  };

  const TabLabel = ({ text }) => (
    <div className="flex items-center justify-center gap-2">
      <span className="text-sm font-medium">{text}</span>
    </div>
  );

  const EmptyStateIcon = ({ tab }) => {
    const iconProps = {
      className: "w-12 h-12 text-slate-700 mx-auto mb-4 dark:text-slate-100",
    };

    switch (tab) {
      case "closed":
        return <CircleCheck {...iconProps} />;
      case "pipeline":
        return <CircleAlert {...iconProps} />;
      default:
        return <CircleAlert {...iconProps} />;
    }
  };
    
  const tabOptions = [
    { label: <TabLabel text="All" />, value: "all" },
    { label: <TabLabel text="In Progress" />, value: "pipeline" },
    { label: <TabLabel text="Converted" />, value: "converted" },
    { label: <TabLabel text="Closed" />, value: "closed" },
  ];

  const onTabChange = (value) => {
    setActiveTab(value);
  };

  const filteredRecords = recordList.items.filter((record) => {
    const isClosed = CLOSED.some((status) => status.value === record.crf_status_id);
      if (activeTab === "pipeline") {return !isClosed;}
      if (activeTab === "closed") {return isClosed;}
      return true;
    });

  return (
  <div className="flex flex-col gap-4">
      <CustomTabs
        tabs={tabOptions}
        height="h-10"
        activeTab={activeTab}
        onChange={onTabChange}
      />
      <div className="py-5 px-4 border rounded-2xl space-y-5">

        {filteredRecords.length > 0 ? (
          filteredRecords.map((record) => {
            const currentId = record.crf_status_id;
            const currentIndex = PIPELINE.findIndex(
              (s) => s.value === currentId
            );
            const isClosed = CLOSED.some(
              (s) => s.value === currentId
            );

        return (
          <Card
            key={record.crf_id}
            className="rounded-2xl border-2 border-slate-200 overflow-hidden 
                      bg-amber-50/30 dark:bg-amber-300/10"
          >
            <div className="flex grid grid-cols-2">
              <div className="title-text pr-5">
                <div className="text-xl font-semibold title-text flex">
                  {record?.crf_contact_name}
                  <div className={`status-indicator ml-6 ${record?.contact_status_label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {record.contact_status_label}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-base">
                    <Building2 className="w-4 h-4" />
                    {record?.crf_company_name} - {record?.crf_industry} 
                  </div>
                  <div className="flex-1 gap-2">
                    {record?.crf_contact_number && (
                      <div className="flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {record.crf_contact_number}
                      </div>
                    )}
                    {record?.crf_email && (
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {record.crf_email}
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {record?.crf_additional_info && (
                    <div className="flex-col items-center gap-3 mt-3">
                      <strong>Additional Info: </strong>
                      {record.crf_additional_info}
                    </div>
                  )}
                  <div className="muted-text text-xs mt-2">Referred by: {record?.referred_by_name}</div>
                </div>
              </div>     
                     
              <div className="flex-1 gap-3">
                <div>
                  <div className="flex items-center">
                    {PIPELINE.map((step, index) => (
                      <React.Fragment key={step.value}>
                        <ButtonWithConfirm
                          key={step.value}
                          disabled={currentId === step.value}
                          className={`
                            w-10 h-10 p-2 rounded-full border-2
                            flex items-center justify-center
                            ${
                              isClosed
                                ? "bg-gray-200 border-gray-300 text-gray-500"
                                : index < currentIndex
                                ? "bg-teal-600 border-teal-600 text-white"
                                : index === currentIndex
                                ? "bg-amber-500 border-amber-400 ring-4 ring-amber-200 text-white scale-110"
                                : "bg-white border-gray-300 text-gray-400"
                            }
                          `}
                          confirmLabel="Update"
                          confirmationTitle="Update Contact Status"
                          confirmationMessage={`Update contact status to "${step.label}"?`}
                          confirmHandler={() => changeStatus(record, step)}
                        >
                          {index + 1}
                        </ButtonWithConfirm>

                        {index !== PIPELINE.length - 1 && (
                          <div
                            className={`h-1 flex-1 transition-all duration-200 ${
                              !isClosed && index < currentIndex
                                ? "bg-teal-600"
                                : "bg-gray-300"
                            }`}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <div className="flex justify-between mt-2 text-xs font-medium">
                    {PIPELINE.map(step => (
                      <div key={step.value} className="w-10 title-text">
                        {step.label}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between w-full mt-3">
                  {CLOSED.map(status => (
                    <ButtonWithConfirm
                      key={status.value}
                      disabled={currentId === status.value}
                      className={`rounded-lg px-4 py-1 text-sm transition ${
                        currentId === status.value
                          ? "bg-red-600 text-white shadow"
                          : "bg-red-50 text-red-900 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-100"
                      }`}
                      confirmLabel="Update"
                      confirmationTitle="Update Contact Status"
                      confirmationMessage={`Update contact status to "${status.label}"?`}
                      confirmHandler={() => changeStatus(record, status)}
                    >
                      {status.label}
                    </ButtonWithConfirm>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <strong className="block title-text">Remarks: </strong>

                  <Input.TextArea
                    defaultValue={record.crf_remarks}
                    autoSize={{ minRows: 1.5, maxRows: 6 }}
                    onBlur={(e) => updateRemarks(record, e.target.value)}
                    maxLength={255}
                    placeholder="Add remarks..."
                    className="bg-gray-50"
                  />
                </div>
              </div>

            </div>
          </Card>
          );
        })
        ) : (
          <div className="bg-white text-slate-900 flex flex-col gap-6 rounded-xl border dark:bg-black/5">
            <div className="px-6 py-12 text-center">
              <EmptyStateIcon tab={activeTab} />
              <p className="text-slate-700 font-medium dark:text-slate-100">
                {activeTab === "all"
                  ? "No client referrals found"
                  : `No ${activeTab.replace("-", " ")} referrals found`}
              </p>
            </div>
          </div>
        )}
    </div>
    <div className="flex justify-end mt-2">
      <Pagination
        current={recordList.pagination.current_page}
        pageSize={recordList.pagination.per_page}
        total={recordList.pagination.total}
        showSizeChanger={false}
        onChange={handlePageChange}
      />
    </div>
  </div>
  );
};

export default ClientReferralList;